import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { SingleValue } from 'react-select';
import { DocumentationTokenProvider } from '../contexts/documentation-token';
import { useAuth } from '../hooks/auth';
import { useDocumentationToken } from '../hooks/documentation-token';
import './CodeModal.scss';
import { DocumentationTokenSelector } from './DocumentationTokenSelector';
import { JsonViewer } from './JsonViewer';
import { Modal } from './Modal';
import { Select, SelectOptionProps } from './Select';
import { Tabs } from './Tabs';
import { Value } from './Value';
import { ValueList } from './ValueList';

export type CodeModalOperation = {
  label: string;
  snippet: ReactNode;
};

type CodeModalProps = {
  title: string;
  data: unknown;
  operations?: CodeModalOperation[];
  show: boolean;
  onClose: () => void;
};

function CodeModalOperations({
  operations,
  selected,
  onSelect,
}: {
  operations: CodeModalOperation[];
  selected: number;
  onSelect: (index: number) => void;
}): ReactElement {
  const { loggedIn } = useAuth();
  const {
    pending: tokensPending,
    tokens,
    selected: selectedToken,
    select: selectToken,
  } = useDocumentationToken();

  const operationOptions: SelectOptionProps<number>[] = useMemo(
    () =>
      operations.map((operation, index) => ({
        label: operation.label,
        value: index,
      })),
    [operations]
  );

  return (
    <div className="code-modal-operations">
      {/* Labels are <label>s for their selects, which also lines them up
      with the selects, as on the editor pages */}
      <ValueList className="code-modal-controls">
        <Value
          label="Operation"
          htmlFor="code-modal-operation-input"
          value={
            <Select
              id="code-modal-operation-input"
              name="code-modal-operation"
              options={operationOptions}
              value={operationOptions[selected]}
              onChange={
                ((option: SingleValue<SelectOptionProps<number>>) => {
                  if (option) {
                    onSelect(option.value);
                  }
                }) as any
              }
            />
          }
        />
        {loggedIn && tokens.length > 0 && (
          <Value
            label="Token"
            htmlFor="code-modal-token-input"
            value={
              <DocumentationTokenSelector
                id="code-modal-token-input"
                tokens={tokens}
                selected={selectedToken}
                onChange={selectToken}
                disabled={tokensPending}
              />
            }
          />
        )}
      </ValueList>
      {operations[selected]?.snippet}
    </div>
  );
}

/**
 * The modal behind a resource page's code button: the resource's raw JSON,
 * and, when there are any, code examples for working with it through the API.
 *
 * The token selector is shared with the documentation. Its tokens are only
 * fetched once the modal has been opened
 */
export function CodeModal({
  title,
  data,
  operations = [],
  show,
  onClose,
}: CodeModalProps): ReactElement {
  const [opened, setOpened] = useState(show);
  const [selectedOperation, setSelectedOperation] = useState(0);

  useEffect(() => {
    if (show) {
      setOpened(true);
    }
  }, [show]);

  useEffect(() => {
    if (selectedOperation >= operations.length) {
      setSelectedOperation(0);
    }
  }, [operations.length, selectedOperation]);

  return (
    <Modal
      className="code-modal"
      show={show}
      closable
      dismissable
      onClose={onClose}
    >
      <h2>{title}</h2>
      {operations.length === 0 ? (
        <JsonViewer src={data} />
      ) : (
        opened && (
          <DocumentationTokenProvider>
            <Tabs
              tabs={[
                {
                  title: 'Data',
                  content: <JsonViewer src={data} />,
                },
                {
                  title: 'Code',
                  content: (
                    <CodeModalOperations
                      operations={operations}
                      selected={selectedOperation}
                      onSelect={setSelectedOperation}
                    />
                  ),
                },
              ]}
            />
          </DocumentationTokenProvider>
        )
      )}
    </Modal>
  );
}
